import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isValidUrl } from "../../utils/validation";
import { message } from "antd";
import { getErrorMessage } from "../../utils/errorUtils";
import { useMemo, useCallback } from "react";
import { BannerMobileService } from "../../services/bannerMobileService";
import { BannerMobileDetail, BannerMobileFormData, BannerMobileListItem, BannerMobileOrderItem, defaultBannerMobileFormData, OperationStatus } from "./constants";

interface BannerMobileContextState {
	// Core Domain Data
	bannerMobileList: BannerMobileListItem[];
	bannerMobileDetail: BannerMobileDetail | null;
	bannerMobileForm: BannerMobileFormData;
	operationStatus: OperationStatus;

	// Setters
	setBannerMobileForm: React.Dispatch<React.SetStateAction<BannerMobileFormData>>;
	clearBannerMobileFormData: () => void;
    clearBannerMobileList: () => void;

	// Actions
	addBannerMobile: () => Promise<void>;
	getBannerMobileList: () => Promise<void>;
	updateBannerMobile: () => Promise<void>;
	removeBannerMobile: (id: string) => Promise<void>;
	getBannerMobileDetails: (id: string) => Promise<void>;
	updateBannerMobileOrders: (orderedList: BannerMobileOrderItem[]) => Promise<void>;
}

interface BannerMobileProviderProps {
    children: React.ReactNode;
}

const BannerMobileContext = createContext<BannerMobileContextState | undefined>(
	undefined
);

export const useBannerMobileContext = () => {
	const context = useContext(BannerMobileContext);
	if (!context) {
		throw new Error(
			"useBannerMobileContext must be used within a BannerMobileContextProvider"
		);
	}
	return context;
};

export const BannerMobileContextProvider: React.FC<BannerMobileProviderProps> = ({
	children,
}) => {
	const navigate = useNavigate();
	const [bannerMobileList, setBannerMobileList] = useState<BannerMobileListItem[]>([]);
	const [bannerMobileDetail, setBannerMobileDetail] = useState<BannerMobileDetail | null>(null);
	const [bannerMobileForm, setBannerMobileForm] = useState<BannerMobileFormData>(defaultBannerMobileFormData);

	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });

	const isBannerMobileDataValid = (data: BannerMobileFormData) => {
		if (!data.bannerMobileLocalizable || data.bannerMobileLocalizable.length === 0) {
			return false;
		}

		if (data.isUpper !== undefined && data.images.image === null) {
			return false;
		}	

		for (const localizable of data.bannerMobileLocalizable) {
			if (!localizable.firstHeader || !localizable.header || !localizable.culture) {
				return false;
			}
			if (data.hasNavigation && !localizable.url) {
				return false;
			}
		}
		return true;
	};

	const hasAnyLocalizable = (localizables: BannerMobileFormData["bannerMobileLocalizable"]) => {
		return localizables.some(localizable => {
			return [localizable.url, localizable.header, localizable.firstHeader]
				.some(value => typeof value === "string" && value.trim().length > 0);
		});
	};

	const clearBannerMobileFormData = () => {
		setBannerMobileForm(defaultBannerMobileFormData);
		setBannerMobileDetail(null);
	};

	const clearBannerMobileList = () => {
		setBannerMobileList([]);
	};

	const hasAllImages = (images: BannerMobileFormData["images"]) => {
		return Object.values(images).every(image => image !== null);
	};

	const hasAnyImage = (images: BannerMobileFormData["images"]) => {
		return Object.values(images).some(image => image !== null);
	};

	const addBannerMobile = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "add" });

		if (!isBannerMobileDataValid(bannerMobileForm) || !hasAllImages(bannerMobileForm.images) || bannerMobileForm.isActive === undefined) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		for (const localizable of bannerMobileForm.bannerMobileLocalizable) {
			if (localizable.url && !isValidUrl(localizable.url)) {
				message.info("Lütfen geçerli bir URL girin.");
				setOperationStatus({ type: "idle" });
				return;
			}
		}

		try {
			const response = await BannerMobileService.addBannerMobile(bannerMobileForm);

			if (response.isSuccessful) {
				message.success("Mobil Banner başarıyla eklendi.");
				setBannerMobileForm(defaultBannerMobileFormData);
				navigate("/mobile-banners");
			} else {
				message.error(response.error?.message || "Beklenmedik Bir Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "add", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [bannerMobileForm, navigate]);


	const getBannerMobileDetails = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await BannerMobileService.getBannerMobileDetails(id);

			if (response.isSuccessful) {
				setBannerMobileDetail({
					bannerId: response.result.bannerId,
					isActive: response.result.isActive,
					isUpper: response.result.isUpper,
					displayOrder: response.result.displayOrder,
					bannerMobileLocalizable: response.result.bannerMobileLocalizable,
					hasNavigation: response.result.hasNavigation,
					imageList: {
						imageUrl: response.result.imageUrl,
					}
				});

				setBannerMobileForm({
					...defaultBannerMobileFormData,
					bannerId: response.result.bannerId,
					bannerMobileLocalizable: defaultBannerMobileFormData.bannerMobileLocalizable.map(localizable => {
						const matched = response.result.bannerMobileLocalizable.find(item => item.culture === localizable.culture);

						if (!matched) {
							return localizable;
						}

						return {
							...localizable,
							bannerLocalizableId: matched.bannerLocalizableId,
							culture: matched.culture,
						};
					}),
				});
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const updateBannerMobile = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!validateMobileBannerId()) {
			return;
		}

		const {
			activated,
			hasNavigationChanged,
			hasUpperChanged,
		} = getMobileBannerChanges();

		if (
			!validateMobileBannerChanges(
				activated,
				hasNavigationChanged,
				hasUpperChanged
			)
		) {
			setOperationStatus({ type: "idle" });
			return;
		}

		const requiresAllUrls =
			hasNavigationChanged && bannerMobileForm.hasNavigation === true;

		if (!validateMobileBannerUrls(requiresAllUrls)) {
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await BannerMobileService.updateBannerMobile(bannerMobileForm);
			const isSuccessful = response.result?.isSuccessful || response.isSuccessful;

			if (isSuccessful) {
				message.success("Mobil Banner başarıyla güncellendi.");
				setBannerMobileForm(defaultBannerMobileFormData);
				setBannerMobileDetail(null);
				navigate("/mobile-banners");
			} else {
				message.error(response.error?.message || response.result?.error?.message || "Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [bannerMobileForm, navigate]);

	const getBannerMobileList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await BannerMobileService.getBannerMobileList();

			if (response.isSuccessful) {
				setBannerMobileList(response.result?.items || []);
				clearBannerMobileFormData();
			} else {
				message.error(response.error ? response.error : "Mobil Banner listesi yüklenirken hata oluştu.");
				setBannerMobileList([]);
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Banner listesi yüklenirken hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [clearBannerMobileFormData]);

	const removeBannerMobile = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await BannerMobileService.removeBannerMobile(id);
			if (response.isSuccessful) {
				message.success("Mobil Banner başarıyla silindi.");
				await getBannerMobileList();
			} else {
				message.error(response.error?.message || "Mobil Banner silinirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "delete", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [getBannerMobileList]);

	const updateBannerMobileOrders = useCallback(async (orderedList: BannerMobileOrderItem[]) => {
		setOperationStatus({ type: "loading", operation: "update" });

		// Separate upper and lower banners
		const upperBannerMobileList = orderedList
			.filter((item) => {
				const bannerMobile = bannerMobileList.find((b) => b.bannerId === item.bannerId);
				return bannerMobile?.isUpper;
			})
			.map((item, index) => ({
				bannerId: item.bannerId,
				displayOrder: index + 1,
			}));

		const lowerBannerMobileList = orderedList
			.filter((item) => {
				const bannerMobile = bannerMobileList.find((b) => b.bannerId === item.bannerId);
				return !bannerMobile?.isUpper;
			})
			.map((item, index) => ({
				bannerId: item.bannerId,
				displayOrder: index + 1,
			}));

		const upperChanged = upperBannerMobileList.some((newItem) => {
			const oldItem = bannerMobileList.find((b) => b.bannerId === newItem.bannerId && b.isUpper);
			return oldItem?.displayOrder !== newItem.displayOrder;
		});

		const lowerChanged = lowerBannerMobileList.some((newItem) => {
			const oldItem = bannerMobileList.find((b) => b.bannerId === newItem.bannerId && !b.isUpper);
			return oldItem?.displayOrder !== newItem.displayOrder;
		});

		if (!upperChanged && !lowerChanged) {
			setOperationStatus({ type: "idle" });
			message.info("Hiçbir değişiklik bulunmamaktadır.");
			return;
		}

		try {
			const response = await BannerMobileService.updateBannerMobileOrders({
				upperBannerMobileList,
				lowerBannerMobileList,
			});
			if (response.isSuccessful) {
				message.success("Mobil Banner sıralaması güncellendi.");
				navigate("/mobile-banners");
			} else {
				message.error(response.error ? response.error.message : "Mobil Banner sıralamaları güncellenirken bir hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [bannerMobileList, navigate]);

	const validateMobileBannerId = () => {
		if (!bannerMobileForm.bannerId) {
			message.info("Banner numarası bulunamadı.");
			setOperationStatus({ type: "idle" });
			return false;
		}

		return true;
	};

	const getMobileBannerChanges = () => {
		const activated =
			bannerMobileForm.isActive !== undefined &&
			bannerMobileForm.isActive !== bannerMobileDetail?.isActive;

		const hasNavigationChanged =
			bannerMobileForm.hasNavigation !== undefined &&
			bannerMobileForm.hasNavigation !== bannerMobileDetail?.hasNavigation;

		const hasUpperChanged =
			bannerMobileForm.isUpper !== undefined &&
			bannerMobileForm.isUpper !== bannerMobileDetail?.isUpper;

		return {
			activated,
			hasNavigationChanged,
			hasUpperChanged,
		};
	};

	const validateMobileBannerChanges = (
		activated: boolean,
		hasNavigationChanged: boolean,
		hasUpperChanged: boolean
	) => {
		if (hasUpperChanged && !hasAnyImage(bannerMobileForm.images)) {
			message.info("Lütfen geçerli bir banner resmi veriniz.");
			return false;
		}

		const hasChanges =
			hasUpperChanged ||
			hasNavigationChanged ||
			hasAnyLocalizable(bannerMobileForm.bannerMobileLocalizable) ||
			hasAnyImage(bannerMobileForm.images) ||
			activated;

		if (!hasChanges) {
			message.info("Değişiklik yapılmadı.");
			return false;
		}

		return true;
	};

	const validateMobileBannerUrls = (requiresAllUrls: boolean) => {
		for (const localizable of bannerMobileForm.bannerMobileLocalizable) {
			const url = localizable.url?.trim() ?? "";

			if (requiresAllUrls && !url) {
				message.info("Lütfen tüm alanları doldurun");
				return false;
			}

			if (url && !isValidUrl(url)) {
				message.info("Lütfen geçerli bir URL giriniz.");
				return false;
			}
		}

		return true;
	};

	const contextValue = useMemo<BannerMobileContextState>(
		() => ({
			bannerMobileForm,
			bannerMobileList,
			bannerMobileDetail,
			setBannerMobileForm,
			operationStatus,
			addBannerMobile,
			removeBannerMobile,
			updateBannerMobileOrders,
			getBannerMobileList,
			getBannerMobileDetails,
			clearBannerMobileFormData,
			clearBannerMobileList,
			updateBannerMobile,
		}),
		[
			bannerMobileForm,
			bannerMobileList,
			bannerMobileDetail,
			setBannerMobileForm,
			operationStatus,
			addBannerMobile,
			removeBannerMobile,
			updateBannerMobileOrders,
			getBannerMobileList,
			getBannerMobileDetails,
			clearBannerMobileFormData,
			clearBannerMobileList,
			updateBannerMobile,
		]
	);

	return (
		<BannerMobileContext.Provider value={contextValue}>
			{children}
		</BannerMobileContext.Provider>
	);
};
